import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { SectionLabel, SectionHeading, PrimaryButton } from '../components/ui/UI';
import { SectionStage } from '../components/SectionStage';
const steps = [{
  n: '01',
  label: 'Connect',
  title: 'Connect',
  body: 'Use API connectors for cloud, identity, SaaS, code, security tools, ticketing, and AI apps. No internal deployment required for the first value moment.'
}, {
  n: '02',
  label: 'Understand',
  title: 'Build the evidence model',
  body: 'Periscan correlates assets, identities, permissions, controls, AI apps, exposures, and remediation history into a working model of your environment.'
}, {
  n: '03',
  label: 'Validate',
  title: 'Run validation missions',
  body: 'Safe missions against exposure, controls, attack paths, AI apps, and fixes — scoped, authorized, and reproducible.'
}, {
  n: '04',
  label: 'Prioritize',
  title: 'Separate real risk from noise',
  body: 'Periscan ranks what is actually reachable and exploitable. Not every "critical" deserves your weekend.'
}, {
  n: '05',
  label: 'Mobilize',
  title: 'Route remediation',
  body: 'Clear fix steps land in the systems your team already uses — Jira, Slack, email — with context attached.'
}, {
  n: '06',
  label: 'Verify',
  title: 'Re-test after remediation',
  body: 'If the risk is gone, you get proof. If it is not, you get the truth. Closed tickets are no longer the finish line.'
}];
const deploy = [{
  title: 'API Connectors',
  body: 'Cloud, identity, SaaS, code, security tools, ticketing, and AI systems. Fast setup. Clear permissions. Easy revocation.'
}, {
  title: 'External Validation Points',
  body: 'Periscan-hosted testing from outside your perimeter. Useful for external exposure, WAF checks, internet-facing assets, email scenarios, and outside-in validation.'
}, {
  title: 'Internal Runner',
  body: 'Optional. Lightweight. Outbound-only. For internal reachability, segmentation, control testing, internal threat paths, and fix verification.'
}];
export function HowItWorks() {
  return <>
      <PageHero eyebrow="How It Works" title="Connect. Validate." titleMuted="Fix. Prove." description="Start agentless with API connectors. Add external validation and an optional internal runner when you want deeper inside-out testing." bg="none" data-id="element-1574" />

      {/* Six-step loop — editorial numbered list, no card chrome, no icons */}
      <SectionStage fx="circuit" data-id="element-1575">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1576">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1577">
            <SectionLabel number="01" label="The Validation Loop" className="mb-8" data-id="element-1578" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-1579">
              <div className="lg:col-span-8" data-id="element-1580">
                <SectionHeading size="xl" data-id="element-1581">
                  Six steps.
                  <br data-id="element-1582" />
                  <span className="text-white/40" data-id="element-1583">One continuous loop.</span>
                </SectionHeading>
              </div>
              <div className="lg:col-span-4 lg:pt-4" data-id="element-1584">
                <p className="text-base text-white/60 leading-relaxed" data-id="element-1585">
                  Validation is not a project. It's a running system that gives
                  you proof on every iteration.
                </p>
              </div>
            </div>

            <ol className="border-t border-white/5" data-id="element-1586">
              {steps.map((s, i) => <motion.li key={s.n} initial={{
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-40px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.04
            }} className="border-b border-white/5 py-10 grid grid-cols-12 gap-6 items-baseline" data-id="element-1587">
                  <span className="col-span-12 md:col-span-1 font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em]" data-id="element-1588">
                    {s.n}
                  </span>
                  <h3 className="col-span-12 md:col-span-4 text-2xl md:text-3xl text-white font-semibold tracking-tight leading-snug" data-id="element-1589">
                    {s.title}
                  </h3>
                  <p className="col-span-12 md:col-span-7 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl" data-id="element-1590">
                    {s.body}
                  </p>
                </motion.li>)}
            </ol>
          </div>
        </section>
      </SectionStage>

      {/* Deployment options — three short editorial blocks, no icons */}
      <SectionStage fx="beams-squares" data-id="element-1591">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1592">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1593">
            <SectionLabel number="02" label="Deployment" className="mb-8" data-id="element-1594" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-1595">
              <div className="lg:col-span-8" data-id="element-1596">
                <SectionHeading size="xl" data-id="element-1597">
                  Start agentless.
                  <br data-id="element-1598" />
                  <span className="text-white/40" data-id="element-1599">Go deeper when ready.</span>
                </SectionHeading>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1600">
              {deploy.map((d, i) => <motion.div key={d.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.08
            }} className="border-t border-white/10 pt-6" data-id="element-1601">
                  <span className="font-mono text-xs text-[#1a8fd4]/70 tracking-[0.2em] block mb-3" data-id="element-1602">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="text-xl font-semibold text-white mb-3 tracking-tight" data-id="element-1603">
                    {d.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed" data-id="element-1604">
                    {d.body}
                  </p>
                </motion.div>)}
            </div>

            <p className="mt-16 text-base text-white/50 max-w-2xl" data-id="element-1605">
              Start simple. Add depth when the business case is obvious.
            </p>

            <div className="mt-10 flex justify-start" data-id="element-1606">
              <PrimaryButton href="#" size="lg" data-id="element-1607">
                Coming Soon
              </PrimaryButton>
            </div>
          </div>
        </section>
      </SectionStage>
    </>;
}